<template>
    <button
        cy-data="submit"
        :type="buttonType"
        :disabled="disabled"
        @click="submitForm"
    >
        <slot />
        {{ buttonValue }}
    </button>
</template>

<script lang="ts">
import { defineComponent, computed, ref, watch, toRefs } from 'vue';
import { useStore } from 'vuex';
import { ResponseStatus } from '@/constants/ajax';
import { useReCaptcha } from 'vue-recaptcha-v3';

export default defineComponent({
    name: 'RecaptchaSubmitButton',
    props: {
        action: {
            type: Number,
            required: true,
        },
        data: {
            type: Object,
            required: true,
        },
        buttonValue: {
            type: String,
            default: '',
        },
        buttonType: {
            type: String,
            default: 'submit',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['click', 'successSubmit'],

    setup(props, { emit }) {
        const store = useStore();
        const response = computed(() => store.state.response);

        const { buttonValue } = toRefs(props);

        const { executeRecaptcha, recaptchaLoaded } = useReCaptcha() as {
            executeRecaptcha: (action: string) => Promise<string>;
            recaptchaLoaded: () => Promise<boolean>;
        };
        const isSubmitting = ref(false);

        watch(response, (newValue) => {
            if (
                isSubmitting.value &&
                newValue.status === ResponseStatus.SUCCESS
            ) {
                emit('successSubmit');
                isSubmitting.value = false;
            }
        });

        const submitForm = async (): Promise<void> => {
            if (props.disabled) return;
            if (isSubmitting.value) return;
            isSubmitting.value = true;
            const recaptchaLoadedApi = recaptchaLoaded as
                | (() => Promise<boolean>)
                | { value: boolean };

            if (
                recaptchaLoadedApi &&
                typeof recaptchaLoadedApi !== 'function' &&
                'value' in recaptchaLoadedApi
            ) {
                recaptchaLoadedApi.value = true;
            }

            const isRecaptchaReady =
                typeof recaptchaLoadedApi === 'function'
                    ? await recaptchaLoadedApi()
                    : Boolean(recaptchaLoadedApi?.value);

            if (isRecaptchaReady) {
                const recaptchaToken =
                    await executeRecaptcha('RecaptchaButton');

                const formPayload = { ...props.data, recaptchaToken };

                const ajaxService = store.state.ajaxService;
                const pending = ajaxService.getPendingValidations();

                if (pending.length > 0) {
                    try {
                        const results = await Promise.all(pending);

                        const hasError = results.some(() => {
                            return;
                        });

                        if (hasError) {
                            isSubmitting.value = false;
                            return;
                        }
                    } catch (e) {
                        isSubmitting.value = false;
                        return;
                    }
                }

                store
                    .dispatch('ajax', {
                        action: props.action,
                        data: formPayload,
                    })
                    .finally(() => {
                        isSubmitting.value = false;
                    });

                emit('click');
            } else {
                console.error('reCAPTCHA nie jest załadowany.');
            }
        };

        return {
            submitForm,
            buttonValue,
        };
    },
});
</script>
